import { useState } from "react";
import axios from "axios";
import { Plus } from "lucide-react";
import { formatApiErrorDetail } from "@/context/AuthContext";
import { Panel } from "@/components/admin/ui";

export default function SolutionCapabilitiesTab({ solution, solutionId, API, onSaved }) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [required, setRequired] = useState(false);
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState("");
  const [togglingId, setTogglingId] = useState(null);
  const capabilities = solution.capabilities || [];

  const addCapability = async () => {
    setAdding(true);
    setError("");
    try {
      await axios.post(`${API}/admin/solutions/${solutionId}/capabilities`, { name, description, required, status: "Active" }, { withCredentials: true });
      setName("");
      setDescription("");
      setRequired(false);
      onSaved();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setAdding(false);
    }
  };

  const toggleCapability = async (capabilityId, currentStatus, isRequired) => {
    if (isRequired) return;
    setTogglingId(capabilityId);
    setError("");
    try {
      await axios.patch(
        `${API}/admin/solutions/${solutionId}/capabilities/${capabilityId}`,
        { status: currentStatus === "Active" ? "Inactive" : "Active" },
        { withCredentials: true }
      );
      onSaved();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setTogglingId(null);
    }
  };

  return (
    <Panel title={`Capabilities in ${solution.name}`} subtitle="These capabilities are included in this solution and can be enabled for organizations.">
      <div className="mb-5 flex flex-wrap items-end gap-2 rounded-xl border border-dashed border-slate-200 p-4 dark:border-white/15">
        <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Capability name" data-testid="add-capability-name" className="rounded-xl border border-slate-300 px-3 py-2 text-sm dark:border-white/15 dark:bg-white/5 dark:text-white dark:placeholder:text-slate-500" />
        <input value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Description" data-testid="add-capability-description" className="min-w-[220px] flex-1 rounded-xl border border-slate-300 px-3 py-2 text-sm dark:border-white/15 dark:bg-white/5 dark:text-white dark:placeholder:text-slate-500" />
        <label className="flex items-center gap-1.5 text-xs font-semibold text-slate-600 dark:text-slate-300">
          <input type="checkbox" checked={required} onChange={(e) => setRequired(e.target.checked)} data-testid="add-capability-required" /> Required
        </label>
        <button type="button" onClick={addCapability} disabled={adding || !name} data-testid="add-capability-btn" className="inline-flex items-center gap-1.5 rounded-xl bg-[#0052FF] px-4 py-2 text-sm font-bold text-white disabled:opacity-50">
          <Plus size={14} /> Add Capability
        </button>
      </div>

      {error && <p className="mb-4 rounded-xl bg-red-50 px-4 py-3 text-sm font-semibold text-red-700 dark:bg-red-500/10 dark:text-red-400" data-testid="capabilities-error">{error}</p>}
      {capabilities.length === 0 ? (
        <p className="text-sm text-slate-500 dark:text-slate-400" data-testid="capabilities-empty">No capabilities configured yet.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[600px] text-left text-sm" data-testid="capabilities-table">
            <thead>
              <tr className="border-b border-slate-200 text-xs uppercase tracking-[0.1em] text-slate-400 dark:border-white/10 dark:text-slate-500">
                <th className="py-3 pr-4 font-bold">Capability</th>
                <th className="py-3 pr-4 font-bold">Description</th>
                <th className="py-3 pr-4 font-bold">Status</th>
                <th className="py-3 font-bold">Required</th>
              </tr>
            </thead>
            <tbody>
              {capabilities.map((c) => (
                <tr key={c.capability_id} className="border-b border-slate-100 last:border-0 dark:border-white/5" data-testid={`capability-row-${c.slug}`}>
                  <td className="py-3 pr-4 font-bold text-slate-950 dark:text-white">{c.name}</td>
                  <td className="py-3 pr-4 text-slate-600 dark:text-slate-400">{c.description}</td>
                  <td className="py-3 pr-4">
                    <button
                      type="button"
                      onClick={() => toggleCapability(c.capability_id, c.status, c.required)}
                      disabled={c.required || togglingId === c.capability_id}
                      data-testid={`capability-toggle-${c.slug}`}
                      className={`h-6 w-11 rounded-full transition-colors duration-200 ${c.status === "Active" ? "bg-emerald-500" : "bg-slate-300 dark:bg-white/15"} ${c.required ? "opacity-60" : ""}`}
                    >
                      <span className={`block h-5 w-5 rounded-full bg-white shadow transition-transform duration-200 ${c.status === "Active" ? "translate-x-5" : "translate-x-0.5"}`} />
                    </button>
                  </td>
                  <td className="py-3 text-slate-600 dark:text-slate-400">{c.required ? "Yes" : "No"}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="mt-3 text-xs text-slate-400 dark:text-slate-500">Required capabilities cannot be disabled.</p>
        </div>
      )}
    </Panel>
  );
}
